import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * Sidebar nav. Supports top-level links + a single level of collapsible
 * children. Active route picked off `useLocation`. Compact (mini) mode
 * collapses labels into the first letter unless the sidebar is hovered.
 */
export function SidebarLinks({ routes, hovered, mini }) {
  const location = useLocation();
  const expanded = mini === false || (mini === true && hovered === true);

  // Strict-prefix match against `<layout><path>`. Substring matching
  // (the previous version) misfired when one route's path was a prefix
  // of another's, and across the regrouped /brand vs /ecommerce splits.
  const isPathActive = (fullPath) => {
    if (!fullPath) return false;
    const here = location.pathname.replace(/\/+$/, '');
    const target = fullPath.replace(/\/+$/, '');
    return here === target || here.startsWith(`${target}/`);
  };

  // For collapse groups, match on the bare group prefix (no layout) — or on
  // any child, since a group's children need not live under its path.
  //
  // "Content library" is the case that forced this: it groups the legacy
  // /dashboard/webpage/<stage> screens, whose URLs were left alone so existing
  // links keep working. Prefix matching alone left the group collapsed and
  // unhighlighted while you were looking at a page inside it.
  const isGroupActive = (groupPath, children) => {
    const prefix = groupPath
      ? (groupPath.startsWith('/') ? groupPath : `/${groupPath}`)
      : '';
    if (prefix && location.pathname.startsWith(prefix)) return true;
    return (children || []).some((c) => {
      if (c.children) return isGroupActive(c.path, c.children);
      return isPathActive(`${c.layout || ''}${c.path}`);
    });
  };

  const renderName = (name) => (expanded ? name : (name?.[0] || ''));

  // Style shared by every active item — colored bg pill + bold text.
  const activeItemSx = {
    bgcolor: 'primary.main',
    color: 'common.white',
    boxShadow: '0 4px 12px rgba(66, 42, 251, 0.25)',
  };

  const createAccordionLinks = (children) => children.map((route, key) => {
    if (route.hide) return null;
    const active = isPathActive(`${route.layout}${route.path}`);
    return (
      <Box
        component={NavLink}
        to={route.layout + route.path}
        key={key}
        sx={{
          textDecoration: 'none',
          // Drop the browser outline only for mouse focus; keyboard
          // focus still gets a clear ring via :focus-visible below.
          outline: 'none',
          display: 'block',
          borderRadius: '10px',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          },
        }}
      >
        <ListItem
          disablePadding
          sx={{
            ml: expanded ? '8px' : '0px',
            mr: expanded ? '8px' : '0px',
            mb: '4px',
            px: '14px',
            py: '8px',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'background-color 120ms ease, color 120ms ease',
            color: active ? 'common.white' : 'text.secondary',
            ...(active ? activeItemSx : {
              '&:hover': { bgcolor: 'action.hover' },
            }),
          }}
        >
          <Typography
            variant='body2'
            sx={{
              fontWeight: active ? 700 : 500,
              color: 'inherit',
            }}
          >
            {renderName(route.name)}
          </Typography>
        </ListItem>
      </Box>
    );
  });

  const createLinks = (rs) => rs.map((route, key) => {
    // Only routes meant for the sidebar carry a `name`. Anything else
    // (storefront pages, OAuth callbacks, the `*` catch-all, etc.)
    // shares this array because React Router reads from the same
    // tree — skip them so they don't stack as invisible rows that
    // push the real menu items down the page.
    if (!route.name || route.hide) return null;
    if (route.collapse) {
      const groupActive = isGroupActive(route.path, route.children);
      return (
        <Accordion
          key={key}
          defaultExpanded={groupActive}
          disableGutters
          elevation={0}
          sx={{
            maxWidth: '100%',
            bgcolor: 'transparent',
            '&:before': { display: 'none' },
            mb: '4px',
          }}
        >
          <AccordionSummary
            expandIcon={expanded ? <ExpandMoreIcon sx={{ color: groupActive ? 'primary.main' : 'text.secondary' }} /> : null}
            sx={{
              borderRadius: '10px',
              px: '12px',
              py: 0,
              minHeight: 44,
              '&:hover': { bgcolor: 'action.hover' },
              '&.Mui-expanded': { minHeight: 44 },
              '& .MuiAccordionSummary-content': { my: '8px' },
            }}
          >
            <Stack direction='row' alignItems='center' spacing='12px' width='100%'>
              {route.icon && (
                <Box
                  sx={{
                    color: groupActive ? 'primary.main' : 'text.secondary',
                    display: 'flex',
                  }}
                >
                  {route.icon}
                </Box>
              )}
              <Typography
                variant='body1'
                sx={{
                  fontWeight: groupActive ? 700 : 500,
                  color: groupActive ? 'text.primary' : 'text.secondary',
                  display: expanded ? 'block' : 'none',
                }}
              >
                {route.name}
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ py: 0, px: 0 }}>
            <List disablePadding>
              {route.icon ? createLinks(route.children) : createAccordionLinks(route.children)}
            </List>
          </AccordionDetails>
        </Accordion>
      );
    }

    const active = isPathActive(`${route.layout || ''}${route.path}`);
    return (
      <Box
        component={NavLink}
        to={(route.layout || '') + route.path}
        key={key}
        sx={{
          textDecoration: 'none',
          outline: 'none',
          display: 'block',
          borderRadius: '10px',
          mx: '8px',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          },
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          spacing='12px'
          sx={{
            mb: '4px',
            px: '14px',
            py: '8px',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'background-color 120ms ease, color 120ms ease',
            color: active ? 'common.white' : 'text.secondary',
            ...(active ? activeItemSx : {
              '&:hover': { bgcolor: 'action.hover' },
            }),
          }}
        >
          {route.icon && (
            <Box sx={{ display: 'flex', color: 'inherit' }}>{route.icon}</Box>
          )}
          <Typography
            variant='body1'
            sx={{
              fontWeight: active ? 700 : 500,
              color: 'inherit',
            }}
          >
            {renderName(route.name)}
          </Typography>
        </Stack>
      </Box>
    );
  });

  return <>{createLinks(routes)}</>;
}

export default SidebarLinks;
